/**
 * A Resource Plan's NOS Deal Stage and Chance of Close (spec Epic 36) — the read and its
 * two writes. Every write invalidates every plan read (Plan History gains a row) and owns
 * its own toast, per .claude/rules/frontend.md.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { planDealStageKeys } from "@/lib/query";
import type { PlanDealStageView } from "@/schemas/plan-deal-stage";
import { send, useInvalidatePlans } from "./use-resource-plans";

export function usePlanDealStage(planId: string) {
  return useQuery({
    queryKey: planDealStageKeys.detail(planId),
    queryFn: () => send<PlanDealStageView>(`/api/plans/${planId}/deal-stage`, "GET"),
  });
}

export function useSetChanceOfClose(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: { percent: number | null; comment: string }) =>
      send<PlanDealStageView>(`/api/plans/${planId}/chance-of-close`, "POST", input),
    onSuccess: (view) => {
      invalidate();
      toast.success(
        view.chanceOfClose.source === "NOS"
          ? `Chance of Close now follows NOS (${view.chanceOfClose.percent ?? "—"}%)`
          : `Chance of Close set to ${view.chanceOfClose.percent ?? "—"}%`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to set the Chance of Close"),
  });
}

export function useSetPlanDeal(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (nymblDealId: string | null) =>
      send<PlanDealStageView>(`/api/plans/${planId}/deal`, "PUT", { nymblDealId }),
    onSuccess: (view) => {
      invalidate();
      toast.success(
        view.deal === null ? "NOS Deal removed from the plan" : `Linked to ${view.deal.dealName}`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to change the NOS Deal"),
  });
}
